import React, { useEffect, useRef, useState } from 'react';
import { store } from 'Models/Store';
import {
	PaymentCheckOutProvider,
	usePaymentCheckOut,
} from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import PaymentTypeWrapper from 'Views/Components/_HumanWritten/Payment/PaymentTypeWrapper';
import { useSetupPayment, SetupPaymentError } from 'Hooks/useSetupPayment';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { MarketingSubscriptionCheckbox } from 'Views/Components/_HumanWritten/Marketing/MarketingSubscriptionCheckbox';
import { acknowledgeMarketingPreference } from 'Services/Api/AccountService';

interface GiftVoucherPaymentStepProps {
	transactionId: string;
	/**
	 * The customer the voucher is being purchased for (set when staff select a customer). Drives
	 * which user's saved cards are offered for "pay with previous card". Falls back to the logged-in
	 * user for self-purchases.
	 */
	customerUserId?: string;
	onSuccess: () => void;
	onProcessingChange?: (processing: boolean) => void;
}

function GiftVoucherPaymentInner({
	transactionId,
	customerUserId,
	onSuccess,
	onProcessingChange,
}: GiftVoucherPaymentStepProps) {
	const {
		transactionDetails: fetchedTransactionDetails,
		error: setupError,
		isLoading: setupLoading,
	} = useSetupPayment({
		transactionId,
		redirectUrl: '',
		isEventBooking: false,
		inCheckIn: false,
	});

	const initializedRef = useRef(false);

	const { initializePaymentFlow, setTransactionDetails, setBeforePayment, paymentProcessing } = usePaymentCheckOut();

	// Marketing acknowledgment is collected only for non-staff self-purchases. State lives
	// here so it's tied to the lifetime of the (mounted) payment step — if the step never
	// renders (e.g. free purchase that skips to success) we never register beforePayment
	// and the user's preference is left untouched.
	const userId = store.userId;
	const showMarketing = !store.isStaff;
	const [marketingAck, setMarketingAck] = useState<boolean | undefined>(undefined);

	useEffect(() => {
		if (!showMarketing) return undefined;
		setBeforePayment(async () => {
			if (marketingAck !== undefined && userId) {
				try {
					await acknowledgeMarketingPreference(userId, marketingAck);
				} catch (err) {
					console.warn('acknowledgeMarketingPreference failed', err);
				}
			}
			return true;
		});
		return () => setBeforePayment(undefined);
	}, [showMarketing, marketingAck, userId, setBeforePayment]);

	useEffect(() => {
		if (transactionId && !initializedRef.current) {
			initializedRef.current = true;
			initializePaymentFlow({
				transactionId,
				userId: customerUserId ?? store.userId,
				paymentLocation: 'gift-certificate',
				includeSubmitButtons: false,
				inCheckIn: false,
				onConfirm: async () => {
					onSuccess();
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [transactionId]);

	useEffect(() => {
		if (fetchedTransactionDetails) {
			setTransactionDetails(fetchedTransactionDetails);
		}
	}, [fetchedTransactionDetails, setTransactionDetails]);

	useEffect(() => {
		onProcessingChange?.(paymentProcessing);
	}, [paymentProcessing, onProcessingChange]);

	if (isNullOrUndefined(transactionId)) {
		return <></>;
	}

	if (setupLoading) {
		return (
			<div className="gift-voucher-payment__loading">
				<LottieSpinner />
			</div>
		);
	}

	if (setupError !== SetupPaymentError.NONE || !fetchedTransactionDetails) {
		return <></>;
	}

	return (
		<div className="gift-voucher-payment">
			<PaymentTypeWrapper />
			{showMarketing && (
				<MarketingSubscriptionCheckbox userId={userId} value={marketingAck} onChange={setMarketingAck} />
			)}
		</div>
	);
}

export function GiftVoucherPaymentStep({
	transactionId,
	customerUserId,
	onSuccess,
	onProcessingChange,
}: GiftVoucherPaymentStepProps) {
	return (
		<PaymentCheckOutProvider>
			<GiftVoucherPaymentInner
				transactionId={transactionId}
				customerUserId={customerUserId}
				onSuccess={onSuccess}
				onProcessingChange={onProcessingChange}
			/>
		</PaymentCheckOutProvider>
	);
}
